import { reaction } from 'mobx';
import { applySnapshot, getParent, getRoot, getSnapshot, onSnapshot } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';

import { BUTTON_TYPES } from '../../../components/ShowContactByDataPoint/constants';
import { reportDncContactViewedClientEvent } from '../../../utils/BIEvents/dnc-event';
import { SEARCH_STATUS } from '../../../utils/constants';
import { formatDate } from '../../../utils/date';
import Contact from '../../Contact';

const Actions = self => {
  let initialState;

  const onSearchStatusChange = () => {
    const { context } = getRoot(self);
    const parentStore = getParent(self);
    const isSearchResolved = self.searchStatus === SEARCH_STATUS.RESOLVED;

    if (isSearchResolved && self.contactsWithSomeDncPhonesCount) {
      reportDncContactViewedClientEvent({
        uniqueSearchId: context.isBulk ? self.uniqueSearchId : '',
        subPlatform: parentStore.userSettings.platform,
        pageUrl: context.url,
        numOfContactsWithDncPhone: self.contactsWithSomeDncPhonesCount,
        numOfContactsWithOnlyDncPhones: self.contactsWithOnlyDncPhonesCount,
        isBulkSearch: true,
      });
    }
  };

  const resetState = state => {
    applySnapshot(self, state);
  };

  return {
    afterCreate: () => {
      initialState = getSnapshot(self);

      reaction(() => self.searchStatus, onSearchStatusChange);
    },

    setCompanyName: companyName => {
      self.companyName = companyName || self.companyName;
    },
    setSearchCompany: (
      {
        // eslint-disable-next-line camelcase
        name,
        domain,
        isRecent = false,
        logoUrl = '',
        companySearchId = '',
        company_lid: companyLid = null,
        url = '',
      },
      rank = 1
    ) => {
      self.companyName = name;
      self.companyUrl = url;
      self.companyDomain = domain;
      self.companyLogoUrl = logoUrl;
      self.isRecent = isRecent;
      self.rank = rank;
      self.companySearchId = companySearchId;
      self.companyLid = companyLid ? parseInt(companyLid, 10) : null;
    },
    setFilters: (type, filters) => {
      switch (type) {
        case 'seniority':
          self.seniorityFilters = filters;
          break;
        case 'department':
          self.departmentsFilters = filters;
          break;
        case 'email':
          self.emailFilters = filters;
          break;
        case 'location':
          self.locationFilters = filters;
          break;
        case 'bulk':
          self.bulkFilters = filters;
          break;
        default:
          break;
      }
    },
    setEmployeeFreeText: value => {
      self.employeeFreeText = value;
    },
    setProspectingActiveTab: value => {
      self.prospectingActiveTab = value;
    },
    setSearchActiveTab: value => {
      self.searchActiveTab = value;
    },
    clearAllFilters: () => {
      self.emailFilters = [];
      self.departmentsFilters = [];
      self.seniorityFilters = [];
      self.locationFilters = [];
    },
    setSenderSearch: ({ sender, html, url, htmlLength, date, messageId, threadViewId }) => {
      self.address = sender.emailAddress;
      self.fullName = sender.name ?? '';
      self.requestId = uuid();
      self.html = html;
      self.htmlLength = htmlLength;
      self.date = formatDate(date);
      self.messageId = messageId;
      self.url = url;
      self.threadViewId = threadViewId;
    },
    setCompanySearchId: setter => {
      self.companySearchId = setter;
    },
    init: (partialSnapshot = {}) => {
      const { searchActiveTab, contactSearchTabEnabled } = self;
      const isSearchByDataPointEnabled = getRoot(self)?.user?.isSearchByDataPointEnabled;
      const selectedDataPointType = isSearchByDataPointEnabled ? BUTTON_TYPES.NONE : BUTTON_TYPES.EMAIL_AND_PHONE;
      resetState({
        ...initialState,
        ...partialSnapshot,
        searchActiveTab,
        contactSearchTabEnabled,
        selectedDataPointType,
      });
    },

    setCompanyPageContacts: (contacts, companyId, uniqueSearchId = '') => {
      contacts.forEach((contact, index) => {
        self.addContact(contact, companyId, index, false, uniqueSearchId);
      });

      self.syncArrayReferences('contacts', 'keyPeople');
    },

    setBulkSearchContacts: contacts => {
      contacts.forEach((contact, index) => {
        const companyId = contact.company_lid?.toString() || contact.companyLid || contact.fqdn;
        contact.id && self.addContact(contact, companyId, index, true);
      });
    },

    addContact: (contact, companyId, index, isBulkSearch = false, uniqueSearchId) => {
      const key = contact.id?.toString(); // search id
      const payload = {
        id: key,
        ...(companyId ? { company: companyId } : {}),
      };
      const employee = Contact.create(payload);
      const ordinalId = contact.ordinalId ? contact.ordinalId : index;
      self.prospectContacts.set(key, employee);
      if (isBulkSearch) {
        employee.setSearchV2Contact({ ...contact, ordinalId });
      } else {
        employee.setContact({
          ...contact,
          isAvailableForUnshownSave: true,
          requestId: self.requestId,
          uniqueSearchId,
          ordinalId,
          ...(contact.contact
            ? {
              revealedBy: contact.contact.revealedBy,
              isMyContact: contact.contact.isMyContact,
            }
            : {}),
        });
      }
    },
    setProspectCompanies: companies => {
      companies.forEach(item => {
        self.addCompany(item);
      });
    },
    addCompany: item => {
      getRoot(self).addCompany(item);
    },
    setCurrentContact: contactId => {
      self.currentContactId = contactId;
    },
    setCurrentCompany: companyId => {
      self.currentCompanyId = companyId;
    },
    setSearchMetadataResult: result => {
      const { sessionId = '', requestId = '', moreResults = true, uniqueSearchId = '', maskId = '' } = result;
      self.maskedId = maskId;
      self.sessionId = sessionId;
      self.requestId = requestId;
      self.uniqueSearchId = uniqueSearchId;
      self.setMoreResults(moreResults);
    },
    clearAllContacts: () => {
      self.prospectContacts.clear();
    },
    clearAllCompanies: () => {
      getRoot(self).clearAllCompanies();
    },
    setSearchStatus: status => {
      self.searchStatus = status;
    },
    getContactById: id => self.prospectContacts.get(id),

    getContactByContactId: contactId => self.contacts.find(contact => contact.contactId === contactId),

    setMoreResults: val => {
      self.moreResults = val;
    },
    setFirstSearch: setter => {
      self.firstSearch = setter;
    },

    setShowEmployeesCounterBadge: setter => {
      self.showEmployeesCounterBadge = setter;
    },

    setKeyPeople: contacts => {
      contacts.forEach(item => {
        self.keyPeople.push(getSnapshot(item));
      });

      self.syncArrayReferences('keyPeople', 'contacts');
    },
    // Make sure contacts and key people are synchronized (saved revealed etc)
    syncArrayReferences: (arrayNameToObserve, arrayNameToUpdate) => {
      self[arrayNameToObserve].forEach(model => {
        onSnapshot(model, newModel => {
          const index = self[arrayNameToUpdate].findIndex(item => item.id === newModel.id);

          if (index > -1) {
            applySnapshot(self[arrayNameToUpdate][index], newModel);
          }
        });
      });
    },

    setNoSearchData: setter => {
      self.noSearchData = setter;
    },
    clearSelectedContacts: () => {
      const currentContacts = Array.from(self.prospectContacts.values());
      currentContacts.map(contact => contact.setIsChecked(false));
    },
    selectAllContacts: () => {
      self.selectableContacts.forEach(contact => {
        contact.setIsChecked(true);
      });
    },
    setCompanyLid: setter => {
      self.companyLid = setter;
    },
    setLushaCompanyId: setter => {
      self.lushaCompanyId = setter;
    },
    setCompanyLids: setter => {
      self.companyLids = setter;
    },
    setUniqueSearchId: uniqueSearchId => {
      self.uniqueSearchId = uniqueSearchId;
    },
    setShowDataPointType: type => {
      self.selectedDataPointType = type;
    },
    setCreditSplitButtonType: type => {
      self.creditSplitClickedButtonType = type;
    },
    setCreditSplitButtonId: id => {
      self.creditSplitClickedButtonId = id;
    },
    setShowCreditSplitPopup: (isOpen, type) => {
      self.showCreditSplitPopup = isOpen;
      self.creditSplitErrorType = type;
    },
    setSearchQuery: searchQuery => {
      self.searchQuery = searchQuery;
    },
    setShouldResearchEmployees: shouldRefresh => {
      self.shouldResearchEmployees = shouldRefresh;
    },
  };
};

export default Actions;
